import React, { useEffect } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Icon,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
  CircularProgress,
  Skeleton,
  Divider,
} from "@mui/material";
import { ProcessedItemData } from "../ProcessedItemFormDialog";
import { kitchenService } from "../../../../services/kitchen.service";
import { toast } from "react-toastify";
import { IESDoc } from "../../../../../../app.interface";
import { isNull } from "lodash";

interface BasicInformationStepProps {
  selectedDocument: any;

  fullDocumnet: any;
  selectedRecipe: any;

  handleUpdateFullDoc: (value: any, field: string) => void;
  setSelectedRecipe: (recipe: any) => void;
}

export const BasicInformationStep: React.FC<BasicInformationStepProps> = ({
  selectedDocument,
  fullDocumnet,
  selectedRecipe,
  handleUpdateFullDoc,
  setSelectedRecipe,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = React.useState(false);
  const [recipes, setRecipes] = React.useState<IESDoc[]>([]);
  const [searchQuery, setSearchQuery] = React.useState("");

  const FetchRecipes = () => {
    setLoading(true);
    kitchenService
      .listRecipes({
        pageIndex: 0,
        pageSize: 25,
        sortField: "created_at",
        sortOrder: "desc",
        searchQuery: searchQuery,
      })
      .then((res) => {
        setRecipes(res.data);
        if (!isNull(selectedDocument) && selectedDocument.recipe_id) {
          const recipe = res.data.find(
            (r) => r.menu_item_id === selectedDocument.recipe_id
          );
          if (recipe) {
            setSelectedRecipe(recipe);
          }
        }
      })
      .catch(() => {
        toast.error("Failed to load recipes");
      })
      .finally(() => {
        setLoading(false);
      });
  };
  useEffect(FetchRecipes, [selectedDocument, searchQuery]);

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: { bg: string; text: string } } = {
      main_course: { bg: "#FEF3C7", text: "#92400E" },
      appetizer: { bg: "#E0E7FF", text: "#3730A3" },
      dessert: { bg: "#FCE7F3", text: "#9D174D" },
      beverage: { bg: "#DBEAFE", text: "#1E40AF" },
      default: { bg: "#F3F4F6", text: "#374151" },
    };
    return colors[category] || colors.default;
  };

  const formatTime = (time: number) => {
    if (time === 0) return "-";
    return `${time} min`;
  };

  const handleRecipeChange = (recipe: IESDoc | null) => {
    setSelectedRecipe(recipe);
    handleUpdateFullDoc(recipe, "recipe_id");
  };

  // setModifiedIngredients(
  //   recipe.ingredients.map((ing) => ({
  //     item_id: ing.item_id,
  //     item_type: ing.item_type,
  //     ingredient_id: ing.ingredient_id,
  //     brand_name: ing.brand_name,
  //     modified_quantity: ing.quantity || 0,
  //     original_quantity: ing.quantity || 0,
  //     unit: ing.unit,
  //     notes: ing.notes || "",
  //     selected_batches: [],
  //     is_modified: false,
  //     required_quantity: (ing.quantity || 0) * multiplier,
  //     remaining_quantity: (ing.quantity || 0) * multiplier,
  //     batches: [],
  //   }))
  // );

  return (
    <Box sx={{ height: "100%" }}>
      <Stack spacing={4}>
        {/* Recipe Selection Section */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 600,
              color: "text.primary",
              mb: 0.5,
            }}
          >
            Recipe Selection
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            Choose a recipe to use as a base for your processed item
          </Typography>

          <Autocomplete
            options={recipes}
            loading={loading}
            getOptionLabel={(option) => option.name}
            value={selectedRecipe}
            onChange={(_, value) => handleRecipeChange(value)}
            onInputChange={(_, value) => setSearchQuery(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search for a recipe..."
                required
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon sx={{ color: "#0891B2" }}>search</Icon>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    "&:hover": {
                      bgcolor: alpha("#0891B2", 0.02),
                    },
                    "&.Mui-focused": {
                      bgcolor: "white",
                      boxShadow: `0 0 0 2px ${alpha("#0891B2", 0.2)}`,
                    },
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  p: 2,
                  cursor: "pointer",
                  borderBottom: `1px solid ${alpha(
                    theme.palette.divider,
                    0.08
                  )}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: alpha("#0891B2", 0.04),
                  },
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha("#0891B2", 0.08),
                      color: "#0891B2",
                    }}
                  >
                    <Icon>restaurant_menu</Icon>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        fontFamily: "Montserrat",
                      }}
                    >
                      {option.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        mb: 1,
                      }}
                    >
                      {option.description || "No description available"}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={option.category.replace(/_/g, " ").toUpperCase()}
                    sx={{
                      bgcolor: getCategoryColor(option.category).bg,
                      color: getCategoryColor(option.category).text,
                      fontWeight: 500,
                      fontSize: "0.75rem",
                      height: 24,
                    }}
                  />
                </Box>

                {/* Details */}
                <Stack
                  direction="row"
                  spacing={2}
                  divider={<Divider orientation="vertical" flexItem />}
                  sx={{
                    px: 1,
                    py: 0.5,
                    bgcolor: alpha("#0891B2", 0.04),
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon sx={{ fontSize: 18, color: "#0891B2" }}>
                      inventory
                    </Icon>
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      Yield: {option.yield_quantity} {option.yield_unit}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon sx={{ fontSize: 18, color: "#0891B2" }}>
                      schedule
                    </Icon>
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      Prep: {formatTime(option.total_preparing_time)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon sx={{ fontSize: 18, color: "#0891B2" }}>timer</Icon>
                    <Typography variant="caption" sx={{ fontWeight: 500 }}>
                      Cook: {formatTime(option.total_cooking_time)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}
            noOptionsText={
              <Box
                sx={{
                  p: 2,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                <Icon
                  sx={{ fontSize: 48, color: alpha("#0891B2", 0.2), mb: 1 }}
                >
                  search_off
                </Icon>
                <Typography variant="body2">No recipes found</Typography>
              </Box>
            }
            loadingText={
              <Box sx={{ p: 2 }}>
                {[1, 2, 3].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 2,
                      alignItems: "center",
                    }}
                  >
                    <Skeleton variant="rectangular" width={48} height={48} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="60%" height={24} />
                      <Skeleton width="40%" height={20} />
                    </Box>
                  </Box>
                ))}
              </Box>
            }
          />
        </Box>

        {/* Name Field Section */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 600,
              color: "text.primary",
              mb: 0.5,
            }}
          >
            Processed Item Name
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            Give your processed item a unique and descriptive name
          </Typography>

          <TextField
            fullWidth
            value={fullDocumnet.name}
            onChange={(e) => handleUpdateFullDoc(e.target.value, "name")}
            placeholder="Enter a name for your processed item..."
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon sx={{ color: "#0891B2" }}>edit</Icon>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: "white",
                "&:hover": {
                  bgcolor: alpha("#0891B2", 0.02),
                },
                "&.Mui-focused": {
                  bgcolor: "white",
                  boxShadow: `0 0 0 2px ${alpha("#0891B2", 0.2)}`,
                },
              },
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
};
